import { useState } from "react";
import { useForm, useController } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import type { DateValueType } from "react-tailwindcss-datepicker";
import useModal from "@/hooks/useModal";
import DateSelect from "@/components/DateSelect";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import { GET_PDR_REQUIREMENTS_QUERY, UPDATE_PDR_REQUIREMENT_DATES_MUTATION } from "../../queries";
import { isGateCohort } from "../pdrGateHelpers";
import {
  RequirementHistoryRowContext,
  UpdateRequirementDatesFormValues,
  validateUpdateRequirementDates
} from "./updateRequirementDatesValidation";

function RequirementDateField({
  label,
  name,
  control,
  minDate,
  maxDate,
  required = true
}: {
  label: string;
  name: keyof UpdateRequirementDatesFormValues;
  control: ReturnType<typeof useForm<UpdateRequirementDatesFormValues>>["control"];
  minDate?: string;
  maxDate: string;
  required?: boolean;
}) {
  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    control,
    name,
    rules: required
      ? {
          required: `${label} is required`
        }
      : undefined
  });

  return (
    <DateSelect
      label={required ? `${label} *` : label}
      value={{ startDate: value || null, endDate: value || null }}
      useModal
      onChange={(nextValue: DateValueType | string) => {
        if (typeof nextValue === "string") return;
        onChange(nextValue?.startDate ?? "");
      }}
      useSingleDate
      useMultiCalendar={false}
      error={!!error}
      errorMessage={error?.message}
      minDate={minDate ? DateTime.fromISO(minDate).toJSDate() : undefined}
      maxDate={DateTime.fromISO(maxDate).toJSDate()}
    />
  );
}

export default function UpdateRequirementDatesModal({
  row,
  siblingRows,
  cycleCode,
  onDismiss
}: {
  row: RequirementHistoryRowContext;
  siblingRows: RequirementHistoryRowContext[];
  cycleCode: string;
  onDismiss: () => void;
}) {
  const { pgaId } = useParams();
  const { Modal } = useModal();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const gateCohort = isGateCohort(row.cohortCode);
  const cycleBoundMinDate = gateCohort ? undefined : row.cycleStartsOn;

  const form = useForm<UpdateRequirementDatesFormValues>({
    defaultValues: {
      requirementStartDate: row.requirementStartDate,
      requirementEndDate: row.requirementEndDate || "",
      evaluationStartDate: row.evaluationStartDate || row.requirementStartDate,
      evaluationEndDate: row.evaluationEndDate || row.requirementEndDate || ""
    },
    mode: "onChange"
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty }
  } = form;

  const [updateRequirementDates] = useMutation(UPDATE_PDR_REQUIREMENT_DATES_MUTATION, {
    refetchQueries: [
      {
        query: GET_PDR_REQUIREMENTS_QUERY,
        variables: {
          pgaId,
          cycleCode
        }
      }
    ]
  });

  const onSubmit = async (values: UpdateRequirementDatesFormValues) => {
    const validationError = validateUpdateRequirementDates(values, row, siblingRows);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitError(null);

    try {
      const { data } = await updateRequirementDates({
        variables: {
          input: {
            pgaId: pgaId!,
            pdpSerial: row.pdpSerial,
            requirementStartDate: values.requirementStartDate,
            requirementEndDate: values.requirementEndDate,
            evaluationStartDate: values.evaluationStartDate,
            evaluationEndDate: values.evaluationEndDate
          }
        }
      });

      if (!data?.updatePdrRequirementDates?.success) {
        setSubmitError(data?.updatePdrRequirementDates?.message || "Unable to update requirement dates.");
        return;
      }

      showSnackbar(data.updatePdrRequirementDates.message);
      onDismiss();
    } catch {
      setSubmitError("Unable to update requirement dates.");
    }
  };

  return (
    <Modal
      headline="Update Dates"
      onClosed={onDismiss}
      actions={
        <>
          <Button variant="text" onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={!isDirty || isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Update requirement and evaluation dates for this PDR history row.
        </p>

        {submitError ? (
          <Banner variant="error" description={submitError} inline closable={false} hideSecondaryDismiss />
        ) : null}

        <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
          <RequirementDateField
            label="Requirement Start Date"
            name="requirementStartDate"
            control={control}
            minDate={cycleBoundMinDate}
            maxDate={row.cycleEndsOn}
          />
          <RequirementDateField
            label="Requirement End Date"
            name="requirementEndDate"
            control={control}
            minDate={row.cycleStartsOn}
            maxDate={row.cycleEndsOn}
          />
        </div>

        <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
          <RequirementDateField
            label="Evaluation Start Date"
            name="evaluationStartDate"
            control={control}
            minDate={cycleBoundMinDate}
            maxDate={row.cycleEndsOn}
          />
          <RequirementDateField
            label="Evaluation End Date"
            name="evaluationEndDate"
            control={control}
            minDate={row.cycleStartsOn}
            maxDate={row.cycleEndsOn}
          />
        </div>
      </div>
    </Modal>
  );
}
